import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { FileUploadModule } from 'primeng/fileupload';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DropdownModule } from 'primeng/dropdown';
import { NGXLogger } from 'ngx-logger';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { Intervento } from '../../../models/intervento.model';
import { InterventiService } from '../../../services/interventi.service';
import { RendicontaInterventoRequest } from '../../../models/requests/rendiconta-intervento.model';
import { AllegatoIntervento } from '../../../models/allegato-intervento.model';
import { Rendicontazione } from '../../../models/rendicontazione.model';

interface Fattura {
  nome: string;
  importoComplessivo: number;
  importoLavoro: number;
  file: File;
}

interface TipoRendicontazione {
  id: number;
  value: string;
  label: string;
  descrizione: string;
}

@Component({
  selector: 'app-rendiconta',
  templateUrl: './rendiconta.component.html',
  styleUrls: ['./rendiconta.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputNumberModule,
    InputTextareaModule,
    FileUploadModule,
    TableModule,
    ToastModule,
    DropdownModule
  ],
  providers: [MessageService]
})
export class RendicontaComponent implements OnInit {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() intervento: Intervento | null = null;
  @Output() rendicontato = new EventEmitter<number>();
  importoRendicontato: number = 0;
  importoFinanziato: number = 0;
  disableSelezioneTipoRendicontazione: boolean = false;

  fatture: Fattura[] = [];
  currentStep: number = 1;
  tipoRendicontazione: TipoRendicontazione | null = null;
  note: string = '';
  salvataggioInCorso: boolean = false;
  tipiRendicontazione: TipoRendicontazione[] = [
    { id: 1, value: 'ACCONTO', label: 'Acconto 50%', descrizione: 'Rendicontazione a titolo di acconto' },
    { id: 2, value: 'SALDO', label: 'Saldo', descrizione: 'Rendicontazione a titolo di saldo' }
  ];

  constructor(
    private messageService: MessageService,
    private logger: NGXLogger,
    private firebaseStorageService: FirebaseStorageService,
    private interventiService: InterventiService
  ) {}

  ngOnInit() {}

  show(intervento: Intervento) {
    this.logger.info('[RENDERICONTA] Mostra rendicontazione', intervento.importoFinanziato);
    this.intervento = intervento;
    this.importoFinanziato = this.intervento?.importoFinanziato ?? 0;
    this.importoRendicontato = this.intervento?.importoRendicontato ?? 0;
    if (this.importoRendicontato && this.importoRendicontato >= 0) {
      this.importoRendicontato = this.importoFinanziato - this.importoRendicontato;
      this.tipoRendicontazione = this.tipiRendicontazione[1]; // Imposta SALDO
      this.disableSelezioneTipoRendicontazione = true;
    }else{
      this.importoRendicontato = 0;
    }
    this.visible = true;
    this.visibleChange.emit(this.visible);
    this.currentStep = 1;

    this.fatture = [];
    this.note = '';
  }

  hide() {
    if (this.salvataggioInCorso) {
      return; // Previeni la chiusura durante il salvataggio
    }
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.intervento = null;
    this.importoRendicontato = 0;
    this.fatture = [];
    this.tipoRendicontazione = null;
    this.currentStep = 1;
    this.note = '';
    this.salvataggioInCorso = false;
  }

  nextStep() {
    if (!this.isStep1Valid()) {
      if (this.isSaldoSuperaFinanziato()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Importo non valido',
          detail: 'Il saldo supera l\'importo finanziato dell\'intervento. Riduci l\'importo per proseguire.',
          life: 4500
        });
      }
      return;
    }
    this.currentStep = 2;
  }

  previousStep() {
    this.currentStep = 1;
  }

  isStep1Valid(): boolean {
    return this.importoRendicontato > 0 &&
           this.importoRendicontato <= this.getImportoMassimoConsentito() &&
           this.tipoRendicontazione !== null &&
           !this.isSaldoSuperaFinanziato();
  }

  getImportoGiaRendicontato(): number {
    return this.intervento?.importoRendicontato || 0;
  }

  getImportoMassimoConsentito(): number {
    if (!this.intervento) return 0;
    if (this.tipoRendicontazione?.value === 'SALDO') {
      return Math.max(this.importoFinanziato - this.getImportoGiaRendicontato(), 0);
    }
    return this.intervento.importoFinanziato || 0;
  }

  isSaldoSuperaFinanziato(): boolean {
    if (!this.intervento || this.tipoRendicontazione?.value !== 'SALDO') {
      return false;
    }
    const totalePostRendicontazione =
      this.getImportoGiaRendicontato() + (this.importoRendicontato || 0);
    return totalePostRendicontazione > (this.importoFinanziato || 0);
  }

  getEccedenzaSaldo(): number {
    if (!this.isSaldoSuperaFinanziato()) {
      return 0;
    }
    return (
      this.getImportoGiaRendicontato() +
      (this.importoRendicontato || 0) -
      (this.importoFinanziato || 0)
    );
  }

  isStep2Valid(): boolean {
    //this.fatture.length > 0 &&
    //return this.calcolaTotaleFatture() === this.importoRendicontato;
    return true;
  }

  onFileSelect(event: any) {
    const files = event.files;
    for (let file of files) {
      this.fatture.push({
        nome: file.name,
        importoComplessivo: 0,
        importoLavoro: 0,
        file: file
      });
    }
  }

  rimuoviFattura(fattura: Fattura) {
    this.fatture = this.fatture.filter(f => f !== fattura);
  }

  onFileUpload(event: any) {
    // Il file è già stato aggiunto alla lista in onFileSelect
    // Qui possiamo aggiungere logica aggiuntiva se necessario
    this.messageService.add({
      severity: 'success',
      summary: 'File caricato',
      detail: 'Il file è stato caricato con successo',
      life: 3000
    });
  }

  calcolaTotaleFatture(): number {
    return this.fatture.reduce((totale, fattura) => totale + (fattura.importoLavoro || 0), 0);
  }

  calcolaTotaleComplessivo(): number {
    return this.fatture.reduce((totale, fattura) => totale + (fattura.importoComplessivo || 0), 0);
  }

  async salvaRendicontazione() {
    if (this.salvataggioInCorso) {
      return; // Previeni click multipli
    }

    if (!this.isStep2Valid()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Il totale delle fatture deve corrispondere all\'importo rendicontato',
        life: 3000
      });
      return;
    }

    this.salvataggioInCorso = true;

    try {
      let tipoRendicontazione: TipoRendicontazione = this.tipiRendicontazione.find(t => t.value === this.tipoRendicontazione?.value)!;
      let request: RendicontaInterventoRequest = {
        tipo: tipoRendicontazione,
        note: this.note,
        importo: this.importoRendicontato,
        allegatiRendicontazione: []
      };
      // Carica tutti i file su Firebase Storage

      this.interventiService.rendiconta(this.intervento!, request).subscribe({
        next: (rendicontazione: Rendicontazione) => {
          const uploadPromises = this.fatture.map(fattura => {
            const path = `interventi/${this.intervento?.id}/rendicontazioni/${rendicontazione?.id}/${fattura.file.name}`;
            let allegato: AllegatoIntervento = {
              id: null,
              nomeFile: fattura.file.name,
              percorsoFile: path,
              tipoFile: fattura.file.type,
              dimensioneKb: fattura.file.size
            };
            request.allegatiRendicontazione.push(allegato);
            return this.firebaseStorageService.uploadFile(fattura.file, path).toPromise();
          });

           Promise.all(uploadPromises);

          this.rendicontato.emit(this.importoRendicontato);

          this.salvataggioInCorso = false;
          this.hide();
          this.reset();
        },
        error: (error: Error) => {
          this.logger.error('Errore durante la modifica dell\'intervento', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Si è verificato un errore durante il salvataggio della rendicontazione',
            life: 5000
          });
          this.salvataggioInCorso = false;
        }
      });
      // Aggiorna l'intervento con i nuovi file caricati
      // Qui implementare la logica di salvataggio

    } catch (error) {
      this.logger.error('Errore durante il salvataggio della rendicontazione', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Si è verificato un errore durante il salvataggio',
        life: 3000
      });
      this.salvataggioInCorso = false;
    }
  }

  reset() {
    this.currentStep = 1;
    this.importoRendicontato = 0;
    this.fatture = [];
    this.tipoRendicontazione = null;
    this.note = '';
    this.salvataggioInCorso = false;
  }

  onTipoRendicontazioneChange(event: any) {
    this.logger.info('[RENDERICONTA] Tipo rendicontazione cambiato', event.value?.value);
    if (event.value?.value === 'ACCONTO' && this.intervento?.importoFinanziato) {
      this.importoRendicontato = Math.round((this.intervento.importoFinanziato * 0.5) * 100) / 100;
    } else {

      if (this.importoRendicontato && this.importoRendicontato != 0) {
        this.importoRendicontato = this.importoFinanziato - this.importoRendicontato;
      } else {
        this.importoRendicontato = this.importoFinanziato;
      }
    }
  }

}
